import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';
import { 
  ShoppingCart, Search, Download, RefreshCw, 
  Eye, Truck, CheckCircle, XCircle, AlertCircle, Package
} from 'lucide-react';

interface Order {
  id: string;
  shopId: string;
  mallOrderId: string;
  orderStatus: 'new' | 'confirmed' | 'printed' | 'shipped' | 'cancelled' | 'returned';
  customerInfo: { name: string; email?: string; phone?: string };
  totalAmount: string;
  orderedAt: string;
  shop: { id: string; shopName: string; platformType: string } | null;
}

interface OrderStats {
  new: number;
  confirmed: number;
  printed: number;
  shipped: number;
  cancelled: number;
  returned: number;
}

const statusConfig: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  new: { label: '新規', color: 'bg-blue-100 text-blue-700', icon: Package },
  confirmed: { label: '確認済', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  printed: { label: '印刷済', color: 'bg-yellow-100 text-yellow-700', icon: Download },
  shipped: { label: '発送済', color: 'bg-purple-100 text-purple-700', icon: Truck },
  cancelled: { label: 'キャンセル', color: 'bg-red-100 text-red-700', icon: XCircle },
  returned: { label: '返品', color: 'bg-gray-100 text-gray-700', icon: RefreshCw },
};

export default function OrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [selectedStatus, startDate, endDate]);

  async function fetchOrders() {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '50',
        ...(selectedStatus && { status: selectedStatus }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(search && { search }),
      });

      const response = await apiFetch(`/api/orders?${params}`);
      const data = await response.json();

      if (!response.ok) throw new Error(data.error);
      setOrders(Array.isArray(data.orders) ? data.orders : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  async function fetchStats() {
    try {
      const response = await apiFetch('/api/orders/stats');
      const data = await response.json();
      if (response.ok) setStats(data.stats);
    } catch (err) {
      console.error('Stats fetch error:', err);
    }
  }

  async function updateStatus(orderId: string, newStatus: string) {
    try {
      const response = await apiFetch(`/api/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }

      fetchOrders();
      fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    }
  }

  async function bulkUpdateStatus(newStatus: string) {
    if (selectedOrders.length === 0) return;

    try {
      const response = await apiFetch('/api/orders/bulk/status', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderIds: selectedOrders, status: newStatus }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }

      setSelectedOrders([]);
      fetchOrders();
      fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    }
  }

  const toggleSelectAll = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(o => o.id));
    }
  };

  // CSV出力機能
  function exportCSV() {
    if (orders.length === 0) return;

    const headers = ['注文番号', '店舗', '顧客名', '金額', 'ステータス', '注文日時'];
    const rows = orders.map(order => [
      order.mallOrderId,
      order.shop?.shopName || '',
      order.customerInfo?.name || '',
      order.totalAmount,
      statusConfig[order.orderStatus]?.label || order.orderStatus,
      new Date(order.orderedAt).toLocaleString('ja-JP'),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `orders_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }

  const toggleSelect = (id: string) => {
    setSelectedOrders(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <ShoppingCart size={28} />
            受注管理
          </h1>
          <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>
            全店舗の受注を一元管理
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchOrders} className="btn-secondary flex items-center gap-2">
            <RefreshCw size={16} />
            更新
          </button>
          <button onClick={exportCSV} className="btn-secondary flex items-center gap-2">
            <Download size={16} />
            CSV出力
          </button>
        </div>
      </div>

      {/* ステータスタブ */}
      {stats && (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedStatus('')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedStatus === '' ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
            }`}
            style={selectedStatus !== '' ? { color: 'var(--text-primary)' } : {}}
          >
            全て ({Object.values(stats).reduce((a, b) => a + b, 0)})
          </button>
          {Object.entries(statusConfig).map(([key, config]) => (
            <button
              key={key}
              onClick={() => setSelectedStatus(key)}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                selectedStatus === key ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
              }`}
              style={selectedStatus !== key ? { color: 'var(--text-primary)' } : {}}
            >
              {config.label} ({stats?.[key as keyof OrderStats] ?? 0})
            </button>
          ))}
        </div>
      )}

      {/* 検索・フィルタ */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 border rounded-lg"
            style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          />
          <span style={{ color: 'var(--text-secondary)' }}>〜</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 border rounded-lg"
            style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          />
        </div>
        <div className="relative flex-1 max-w-md">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <Search size={18} />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchOrders()}
            placeholder="注文番号、顧客名で検索..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
            style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          />
        </div>
        <button onClick={fetchOrders} className="btn-primary">反映</button>
      </div>

      {/* 一括操作 */}
      {selectedOrders.length > 0 && (
        <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
          <span className="font-medium text-blue-700">{selectedOrders.length}件選択中</span>
          <div className="flex gap-2">
            <button onClick={() => bulkUpdateStatus('confirmed')} className="px-3 py-1 bg-green-600 text-white rounded text-sm">確認済みにする</button>
            <button onClick={() => bulkUpdateStatus('printed')} className="px-3 py-1 bg-yellow-600 text-white rounded text-sm">印刷済みにする</button>
            <button onClick={() => bulkUpdateStatus('shipped')} className="px-3 py-1 bg-purple-600 text-white rounded text-sm">発送済みにする</button>
            <button onClick={() => setSelectedOrders([])} className="px-3 py-1 bg-gray-400 text-white rounded text-sm">選択解除</button>
          </div>
        </div>
      )}

      {/* エラー */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">×</button>
        </div>
      )}

      {/* 受注一覧 */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedOrders.length === orders.length && orders.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>注文番号</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>店舗</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>顧客名</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>金額</th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>ステータス</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>注文日時</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>操作</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center" style={{ color: 'var(--text-secondary)' }}>
                    受注がありません
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const config = statusConfig[order.orderStatus];
                  return (
                    <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(order.id)}
                          onChange={() => toggleSelect(order.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm" style={{ color: 'var(--text-primary)' }}>
                          {order.mallOrderId}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                          {order.shop?.shopName || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                          {order.customerInfo?.name || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          ¥{Number(order.totalAmount).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${config?.color}`}>
                          {config?.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {new Date(order.orderedAt).toLocaleString('ja-JP')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setDetailOrder(order)}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                            title="詳細"
                          >
                            <Eye size={16} style={{ color: 'var(--text-secondary)' }} />
                          </button>
                          <select
                            value={order.orderStatus}
                            onChange={(e) => updateStatus(order.id, e.target.value)}
                            className="text-sm px-2 py-1 border rounded"
                            style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                          >
                            {Object.entries(statusConfig).map(([key, cfg]) => (
                              <option key={key} value={key}>{cfg.label}</option>
                            ))}
                          </select>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* 詳細モーダル */}
      {detailOrder && (
        <OrderDetailModal order={detailOrder} onClose={() => setDetailOrder(null)} />
      )}
    </div>
  );
}

// 詳細モーダル
function OrderDetailModal({ order, onClose }: { order: Order; onClose: () => void }) {
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDetail();
  }, [order.id]);

  async function fetchDetail() {
    try {
      const response = await apiFetch(`/api/orders/${order.id}`);
      const data = await response.json();
      if (response.ok) setDetail(data.order);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl" style={{ backgroundColor: 'var(--background-card)' }}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
              受注詳細: {order.mallOrderId}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">×</button>
          </div>

          {loading ? (
            <div className="text-center py-8">読み込み中...</div>
          ) : detail ? (
            <div className="space-y-6">
              {/* 顧客情報 */}
              <div>
                <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>顧客情報</h3>
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                  <p><strong>氏名:</strong> {detail.customerInfo?.name}</p>
                  <p><strong>電話:</strong> {detail.customerInfo?.phone || '-'}</p>
                  <p><strong>メール:</strong> {detail.customerInfo?.email || '-'}</p>
                </div>
              </div>

              {/* 配送先 */}
              {detail.shippingAddress && (
                <div>
                  <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>配送先</h3>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                    <p><strong>氏名:</strong> {detail.shippingAddress.name}</p>
                    <p><strong>住所:</strong> 〒{detail.shippingAddress.postalCode} {detail.shippingAddress.address}</p>
                  </div>
                </div>
              )}

              {/* 商品 */}
              <div>
                <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>商品</h3>
                <table className="w-full">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                      <th className="px-3 py-2 text-left text-xs">商品名</th>
                      <th className="px-3 py-2 text-right text-xs">単価</th>
                      <th className="px-3 py-2 text-right text-xs">数量</th>
                      <th className="px-3 py-2 text-right text-xs">小計</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.items?.map((item: any) => (
                      <tr key={item.id} className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                        <td className="px-3 py-2 text-sm">{item.productName}</td>
                        <td className="px-3 py-2 text-sm text-right">¥{Number(item.unitPrice).toLocaleString()}</td>
                        <td className="px-3 py-2 text-sm text-right">{item.quantity}</td>
                        <td className="px-3 py-2 text-sm text-right font-medium">¥{Number(item.lineSubtotal).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} className="px-3 py-2 text-right font-semibold">合計</td>
                      <td className="px-3 py-2 text-right font-bold text-lg">¥{Number(detail.totalAmount).toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* 配送情報 */}
              {detail.shipments?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>配送情報</h3>
                  {detail.shipments.map((ship: any) => (
                    <div key={ship.id} className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                      <p><strong>追跡番号:</strong> {ship.trackingNumber || '-'}</p>
                      <p><strong>配送業者:</strong> {ship.carrierName || '-'}</p>
                      <p><strong>発送日:</strong> {ship.shippedAt ? new Date(ship.shippedAt).toLocaleString('ja-JP') : '-'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">詳細を取得できませんでした</div>
          )}

          <div className="flex justify-end mt-6">
            <button onClick={onClose} className="btn-secondary">閉じる</button>
          </div>
        </div>
      </div>
    </div>
  );
}

